import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../../../../../packages/kiti/components/structure/Grid'
import { getGetSingleConditionValue } from '../../../_helpers/singleConditionValue'
import { useFormState } from '../../../../../../packages/kiti/components/form/form/FormHandler'
import { ACTIVITY_TAG_TYPE } from '../../../../../tags/_constants'
import AssetSelect from '../../../../../../containers/assets/select/AssetSelect'
import ChecklistSelect from '../../../../../../containers/checklists/select/ChecklistSelect'
import EditableSentence from '../../EditableSentence'
import GalleryInput from '../../../../../../components/form/GalleryInput'
import FormItem from '../../../../../../packages/kiti/components/form/form/FormItem'
import Modal from '../../../../../../components/structure/Modal'
import PlaceSelect from '../../../../../../containers/places/select/PlaceSelect'
import PrioritySelect from '../../../../../../containers/serviceOrders/select/PrioritySelect'
import ServiceOrderStatusSelect from '../../../../../../containers/statuses/select/ServiceOrderStatusSelect'
import Select from '../../../../../../packages/kiti/components/form/Select'
import TagSelect from '../../../../../../containers/tags/select/TagSelect'
import SubmitBar from '../../../../../../packages/kiti/components/form/SubmitBar'
import Text, { Title } from '../../../../../../packages/kiti/components/presentation/Text'
import Separator from '../../../../../../packages/kiti/components/helpers/Separator'
import Line from '../../../../../../packages/kiti/components/helpers/Line'
import DateInputWithInterpolations from '../../../../../../packages/kiti-editor/components/date/DateInputWithInterpolations'
import InputWithInterpolations from '../../../../../../packages/kiti-editor/components/input/InputWithInterpolations'
import UsersOrGroupsSelect from '../../../../../../containers/users/select/UsersOrGroupsSelect'
import AddressInput from '../../../../../../components/form/AddressInput'
import FieldValuesFormSection from '../../../../../../containers/fieldValues/form/FieldValuesFormSection'
import { useInterpolations } from '../../../../../../utils/interpolations/interpolations'
import InfoLabel, { InfoRow } from './InfoLabel'
import DescriptionEditor from './DescriptionEditor'
import { useRawInterpolations } from '../../../../../../utils/interpolations/rawInterpolations'

/**
 * Sentença da ação `update_child_service_order` ("Atualizar Atividade"): atualiza a(s) Atividade(s) filha(s)
 * vinculada(s) à Atividade onde a automação disparou OU a própria Atividade que disparou a automação,
 * conforme `payload.target` ("child" padrão | "current"; automações salvas sem o campo reabrem como
 * "child"). Com "current", o seletor "Quais atividades filhas atualizar" (`checklists`) não se aplica e fica
 * escondido. O campo opcional Status reaproveita `ServiceOrderStatusSelect` (o mesmo de `UpdateStatusSentence`)
 * e vale para os dois destinos. Todos os campos são opcionais — só entram no
 * payload enviado ao backend os campos preenchidos (ver `cleanRequest.js`,
 * `cleanUpdateChildServiceOrderPayload`), no mesmo espírito de `UpdatePlaceSentence`/`LockPlaceSentence`.
 *
 * O seletor de questionários reaproveita exatamente o mesmo componente/interação de
 * `complete_service_orders` (`ChecklistSelect` com `multiple`), pois o backend espelha
 * intencionalmente o formato do payload dessa ação para o campo `checklists`.
 *
 * Os demais campos novos (responsável, ativo, prioridade, endereço, metadata, nome e campos
 * adicionais) reaproveitam exatamente os mesmos componentes usados em `InsertServiceOrderSentence`
 * (ação "criar uma nova atividade") para os campos que essa tela já tem, e em `GeneralSection`
 * (tela real de criação/edição de Atividade, `serviceOrders/editV2`) para prioridade e endereço,
 * que `InsertServiceOrderSentence` ainda não expõe.
 *
 * Responsável "Automático", Tag e Documentos: o responsável reaproveita a opção "Automático" do cadastro de
 * Atividade (`UsersOrGroupsSelect` com `showAutomaticOption`, guardando `payload.automatic`), a tag usa o mesmo
 * `TagSelect` (tag de atividade) de `AssignTagSentence`/`GeneralSection` e os documentos o mesmo `GalleryInput` de
 * `insertServiceOrder/FieldsSection`. Campos adicionais e documentos são MESCLADOS pelo backend (os que não foram
 * configurados aqui continuam na atividade filha); a tag SUBSTITUI a atual.
 *
 * "Campos adicionais" (`field_values`) reaproveita `FieldValuesFormSection` com
 * `origin="service_orders"`, sem nenhum filtro por questionário: confirmado lendo
 * `useFieldsFromApi`/`FieldsLoader` que o catálogo de campos adicionais é por `origin` (igual ao
 * usado em `InsertServiceOrderSentence`/`FieldsSection` e na tela real de Atividade,
 * `serviceOrders/editV2/sections/FieldsSection`), não por `checklist_id` — portanto não há
 * necessidade (nem como implementar de forma correta) de esconder esta seção quando 0/múltiplos
 * questionários estiverem selecionados em `checklists`: o conjunto de campos adicionais de uma
 * Atividade nunca dependeu de qual questionário ela usa.
 */
export const TARGET_CURRENT = 'current'
export const TARGET_CHILD = 'child'

// Destino da atualização: ausente (automações antigas) ou qualquer valor diferente de "current" = filha.
export const getUpdateTarget = payload => (payload?.target === TARGET_CURRENT ? TARGET_CURRENT : TARGET_CHILD)

// Seletor de destino; mostra "Atividade filha vinculada" quando ainda não há valor salvo (padrão).
const TargetSelect = ({ value, ...props }) => <Select {...props} value={value || TARGET_CHILD} />

// "Responsável" conta como preenchido com usuário/grupo escolhido (lista vazia não conta) ou "Automático".
const hasResponsible = payload => {
  const userOrGroup = payload?.user_or_group
  const hasUserOrGroup = Array.isArray(userOrGroup) ? userOrGroup.length > 0 : !!userOrGroup
  return hasUserOrGroup || !!payload?.automatic
}

const UpdateChildServiceOrderSentence = () => {
  const { t } = useTranslation('triggers')
  const [open, setOpen] = React.useState()
  const { form } = useFormState()
  const canUseAutomaticDistribution = useSessionPermission('useAutomaticDistribution')
  // Mantém a opção visível numa automação já salva como "Automático", mesmo se quem edita não tiver a permissão.
  const showAutomaticOption = canUseAutomaticDistribution || !!form.payload?.automatic

  const checklistId = getGetSingleConditionValue(form.conditions, 'checklist_id')
  const interpolations = useInterpolations(form.entity, checklistId)
  const rawInterpolations = useRawInterpolations(form.entity, checklistId)

  const target = getUpdateTarget(form.payload)
  const isCurrent = target === TARGET_CURRENT
  const targetOptions = [
    { id: TARGET_CURRENT, name: t('updateChildServiceOrder.target.current') },
    { id: TARGET_CHILD, name: t('updateChildServiceOrder.target.child') }
  ]

  const dataToChange = () => {
    const values = []
    if (form.payload?.name) values.push(t('updateChildServiceOrder.name'))
    if (form.payload?.description) values.push(t('updateChildServiceOrder.description'))
    if (form.payload?.place) values.push(form.payload.place.name || t('updateChildServiceOrder.place'))
    if (form.payload?.asset) values.push(t('updateChildServiceOrder.asset'))
    if (form.payload?.scheduling) values.push(t('updateChildServiceOrder.scheduling'))
    if (hasResponsible(form.payload)) values.push(t('updateChildServiceOrder.user'))
    if (form.payload?.tag) values.push(t('updateChildServiceOrder.tag'))
    if (form.payload?.priority) values.push(t('updateChildServiceOrder.priority'))
    if (form.payload?.status?.id) values.push(form.payload.status.label || t('updateChildServiceOrder.status'))
    if (form.payload?.address?.search) values.push(t('updateChildServiceOrder.address'))
    if (form.payload?.metadata) values.push(t('updateChildServiceOrder.metadata'))
    if (form.payload?.field_values?.some(fv => fv?.value)) values.push(t('fieldsSection.additionalFields'))
    if (form.payload?.documents?.length) values.push(t('updateChildServiceOrder.documents'))
    if (!isCurrent && form.payload?.checklists?.length) values.push(t('updateChildServiceOrder.checklists'))

    if (!values.length) return '_______'
    return values.join(', ')
  }

  return (
    <>
      <Text size="h2">{t('updateChildServiceOrder.with')}</Text>
      <EditableSentence label={dataToChange()} truncate={30} onClick={() => setOpen(true)} field="payload" />

      <Modal open={open} onClose={() => setOpen(false)} width="42rem">
        <InfoRow info={t('fieldsWithoutValueNotChanged')}>
          <Title size="h5" bold marginBottom={0}>
            {t('actions.update_child_service_order')}
          </Title>
        </InfoRow>
        <Separator weight={4} />

        <Grid paddingGutter={0.5} wrap>
          <Col width="100%">
            <InfoLabel
              label={t('updateChildServiceOrder.target')}
              info={t(
                isCurrent ? 'updateChildServiceOrder.targetHelp.current' : 'updateChildServiceOrder.targetHelp.child'
              )}
            />
            <FormItem
              ignoreLabel
              path={['payload', 'target']}
              Input={TargetSelect}
              options={targetOptions}
              selectOnlyId
              disableEmptyOption
            />
          </Col>

          <Col width="100%">
            <FormItem
              label={t('updateChildServiceOrder.name')}
              path={['payload', 'name']}
              Input={InputWithInterpolations}
              interpolations={interpolations}
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.place')}
              path={['payload', 'place']}
              Input={PlaceSelect}
              interpolations={rawInterpolations}
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.asset')}
              path={['payload', 'asset']}
              Input={AssetSelect}
              filter={{ placeIds: form?.payload?.place?.id }}
              disabled={!form?.payload?.place?.id}
              interpolations={rawInterpolations}
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.scheduling')}
              path={['payload', 'scheduling']}
              Input={DateInputWithInterpolations}
              interpolations={rawInterpolations}
              useTime
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.priority')}
              path={['payload', 'priority']}
              Input={PrioritySelect}
            />
          </Col>

          <Col width="100%">
            <FormItem
              label={t('updateChildServiceOrder.user')}
              path={['payload', 'user_or_group']}
              Input={UsersOrGroupsSelect}
              interpolations={rawInterpolations}
              showAutomaticOption={showAutomaticOption}
              automatic={!!form.payload?.automatic}
              automaticPath={['payload', 'automatic']}
              userOrGroupPath={['payload', 'user_or_group']}
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.tag')}
              path={['payload', 'tag']}
              Input={TagSelect}
              type={ACTIVITY_TAG_TYPE}
              allowCreate={false}
            />
          </Col>

          <Col width="50%">
            <FormItem
              label={t('updateChildServiceOrder.status')}
              path={['payload', 'status']}
              Input={ServiceOrderStatusSelect}
            />
          </Col>

          <Col width="100%">
            <FormItem label={t('updateChildServiceOrder.address')} path={['payload', 'address']} Input={AddressInput} />
          </Col>

          <Col width="100%">
            <FormItem
              label={t('updateChildServiceOrder.metadata')}
              path={['payload', 'metadata']}
              Input={InputWithInterpolations}
              interpolations={rawInterpolations}
            />
          </Col>

          <Col width="100%">
            <Separator weight={2} />
            <InfoRow
              info={t(isCurrent ? 'updateChildServiceOrder.fieldsHelpCurrent' : 'updateChildServiceOrder.fieldsHelp')}
            >
              <Title size="xxsmall" bold color="dark3" marginBottom={0}>
                {t('fieldsSection.additionalFields')}
              </Title>
            </InfoRow>
            <Line />
            <Separator weight={4} />
            <FieldValuesFormSection
              origin="service_orders"
              path={['payload', 'field_values']}
              hideTitle
              isPropSnakeCase
            />
          </Col>

          <Col width="100%">
            <FormItem
              label={t('updateChildServiceOrder.description')}
              path={['payload', 'description']}
              Input={DescriptionEditor}
              interpolations={interpolations}
            />
          </Col>

          <Col width="100%">
            <InfoLabel
              label={t('updateChildServiceOrder.documents')}
              info={t(
                isCurrent ? 'updateChildServiceOrder.documentsHelpCurrent' : 'updateChildServiceOrder.documentsHelp'
              )}
            />
            <FormItem
              ignoreLabel
              path={['payload', 'documents']}
              Input={GalleryInput}
              buttonLabel={t('fieldsSection.addFile')}
            />
          </Col>

          {!isCurrent && (
            <Col width="100%">
              <InfoLabel
                label={t('updateChildServiceOrder.checklists')}
                info={[t('updateChildServiceOrder.checklistsHelp'), t('updateChildServiceOrder.dependentHelp')]}
              />
              <FormItem
                ignoreLabel
                path={['payload', 'checklists']}
                Input={ChecklistSelect}
                filter={{ usages: ['serviceOrder'], includeDisabled: false }}
                multiple
              />
            </Col>
          )}
        </Grid>
        <SubmitBar onClick={() => setOpen(false)} />
      </Modal>
    </>
  )
}

export default UpdateChildServiceOrderSentence
